<template>
  <Modal :value="value" @input="closeModal" width="lg">
    <div slot="header">
      Editar serviços em lote — {{ accountName }} ({{ vehicles.length }} veículo(s))
      <InfoIcon size="1x" class="hint" v-tooltip.auto="tip(scopeHint)" />
    </div>

    <template v-if="report">
      <div v-if="aborted" class="alert alert-danger">{{ aborted }}</div>

      <ReportBlock title="Aplicado" tone="ok" :items="report.applied" />
      <ReportBlock title="Falhou" tone="error" :items="failedItems" />
      <ReportBlock title="Pendentes" tone="warn" :items="pendingItems" />
      <ReportBlock title="Já estavam como você quer" :items="report.unchanged" />
    </template>

    <template v-else>
      <!-- Produtos do veículo -->
      <p class="block-title">Produtos do veículo</p>

      <div class="line">
        <button class="btn btn-sm state" :class="intentClass(cronoIntent)" @click="cycleCrono">
          {{ intentLabel(cronoIntent) }}
        </button>
        <span class="tag-slot"></span>
        <span class="label">Cronotacógrafo</span>
        <InfoIcon size="1x" class="hint" v-tooltip.auto="tip(cronoHint)" />
        <span class="filler"></span>
        <small class="count" :class="{ 'text-warning': mixed(productSummary.cronoCount) }">
          {{ consolidated(productSummary.cronoCount) }}
        </small>
      </div>

      <div class="line">
        <button class="btn btn-sm state" :class="sneModule ? 'btn-primary' : 'btn-outline'"
          :disabled="!sneModule" title="Desfazer — não mexer no SNE" @click="clearSne">
          {{ sneModule ? 'Registrar' : 'Manter' }}
        </button>
        <span class="tag-slot"></span>
        <span class="label">SNE</span>
        <InfoIcon size="1x" class="hint" v-tooltip.auto="tip(sneHint)" />
        <select v-model="sneModule" @change="pickSneModule" class="browser-default custom-select custom-select-sm module">
          <option value="">Qual módulo o cliente contratou?</option>
          <option v-for="mod in sneModules" :key="mod.name" :value="mod.name">
            {{ mod.friendlyName || mod.name }}
          </option>
        </select>
        <span class="filler"></span>
        <small class="count" :class="{ 'text-warning': mixed(productSummary.sneCount) }">
          {{ consolidated(productSummary.sneCount) }}
        </small>
      </div>

      <!-- Serviços -->
      <p class="block-title mt-3">Robôs</p>
      <div class="line search">
        <input class="input form-control form-control-sm" v-model="generic"
          placeholder="Pesquisar por nome do serviço">
        <button class="btn btn-sm btn-link nowrap" v-if="hasIntent" @click="clearAll">Limpar alterações</button>
      </div>

      <div class="services-list">
        <div v-for="summary in filteredSummaries" :key="summary.name" class="line service-row">
          <button class="btn btn-sm state" :class="intentClass(intents[summary.name])"
            @click="cycleIntent(summary.name)">
            {{ intentLabel(intents[summary.name]) }}
          </button>
          <span class="tag-slot">
            <span v-if="summary.uf" class="badge badge-secondary">{{ summary.uf }}</span>
          </span>
          <span class="label">{{ summary.friendlyName }}</span>
          <InfoIcon size="1x" class="hint" v-tooltip.auto="tip(serviceHint(summary))" />
          <span class="filler"></span>
          <small class="count" :class="{ 'text-warning': summary.state === 'mixed' }">
            {{ consolidated(summary.enabledCount) }}
          </small>
        </div>
        <p v-if="filteredSummaries.length === 0" class="text-center text-muted my-3">Nenhum serviço encontrado</p>
      </div>

      <!-- Resumo -->
      <div class="summary">
        <p v-if="!hasIntent" class="text-muted mb-0">
          Clique em <strong>Manter</strong> para alternar entre ligar e desligar. O que você não
          tocar fica como está em cada veículo.
        </p>
        <template v-else>
          <p v-for="frase in productPhrases" :key="frase" class="mb-1">
            <strong class="text-info">Produto</strong> {{ frase }}
            <small class="text-muted">nos {{ vehicles.length }} veículos</small>
          </p>
          <p v-if="toEnable.length" class="mb-1">
            <strong class="text-success">Ligar</strong> {{ toEnable.join(', ') }}
            <small class="text-muted">nos {{ vehicles.length }} veículos</small>
          </p>
          <p v-if="toDisable.length" class="mb-1">
            <strong class="text-danger">Desligar</strong> {{ toDisable.join(', ') }}
            <small class="text-muted">nos {{ vehicles.length }} veículos</small>
          </p>
          <small class="text-muted">
            Os demais serviços não mudam
            <InfoIcon size="1x" class="hint" v-tooltip.auto="tip(keepHint)" />
          </small>
        </template>
      </div>
    </template>

    <div slot="footer">
      <button v-if="report" class="btn btn-outline" @click="report = null">Voltar para a edição</button>
      <template v-else>
        <button class="btn btn-outline mr-2" @click="closeModal">Cancelar</button>
        <button class="btn btn-primary" :disabled="!hasIntent || !!progress" @click="apply">
          <template v-if="progress">Aplicando {{ progress.done }}/{{ progress.total }}</template>
          <template v-else>Aplicar em {{ vehicles.length }} veículos</template>
        </button>
      </template>
    </div>
  </Modal>
</template>

<script>
import Modal from '@/components/shares/specialTag/Modal'
import ReportBlock from '@/components/admin/vehicles/BulkServicesReportBlock'
import Sorter from '@/models/Sorter'
import { InfoIcon } from 'vue-feather-icons'

/** Apps que a tela exibe. cm-taxas fica fora: o backend também recusa a intenção. */
const APP_NAMES = ['cm-frotas', 'cm-crono']
const FROTAS_APP = 'cm-frotas'
const CRONO_APP = 'cm-crono'
const SNE_MODULES = ['autos_sne_denatran20', 'autos_sne_denatran40']
const CREDENTIAL_FIELDS = ['login', 'password', 'cpf', 'cnpj', 'cpf_cnpj', 'certificate_base64']

/** Veículos por requisição — fatia só para o progresso avançar numa frota grande. */
const CHUNK = 25

export default {
  components: { InfoIcon, Modal, ReportBlock },
  computed: {
    scopeHint() {
      return 'Mostra os robôs de Multas (Frotas) e de Cronotacógrafo. Os robôs de Taxas não ' +
        'aparecem aqui e não podem ser alterados por esta tela — desligar um deles por engano ' +
        'interromperia a captura de licenciamento e IPVA do cliente.'
    },
    cronoHint() {
      return 'Ativar liga os robôs de Cronotacógrafo nos veículos selecionados. Remover desliga. ' +
        'É isso que marca o veículo como sendo do produto Crono.'
    },
    sneHint() {
      return 'Registre o módulo que o cliente contratou. O módulo escolhido é ligado e os robôs de ' +
        'multa do Frotas que ele substitui — os nacionais e os das UFs dos veículos selecionados — ' +
        'ficam marcados para desligar na lista abaixo, onde você pode desmarcar o que quiser manter. ' +
        'Nenhum outro robô da família SNE é desligado. Para desfazer, clique no botão Registrar.'
    },
    keepHint() {
      return 'Todo robô que você não tocou continua exatamente como está em cada veículo, inclusive ' +
        'a credencial já vinculada. Veículo cujo robô a ligar exija uma credencial que a conta não ' +
        'tenha fica pendente e não é alterado.'
    },
    sneModules() {
      return Sorter.sortBy(
        this.scrapingServices.filter(s => s.appName === FROTAS_APP && SNE_MODULES.includes(s.name)),
        'friendlyName',
        { sensitivity: false }
      )
    },
    sneModuleLabel() {
      const mod = this.sneModules.find(m => m.name === this.sneModule)
      return mod ? (mod.friendlyName || mod.name) : ''
    },
    /** Estado consolidado dos produtos, lido dos próprios robôs do veículo. */
    productSummary() {
      const cronoNames = this.scrapingServices.filter(s => s.appName === CRONO_APP).map(s => s.name)

      return {
        cronoCount: this.vehicles.filter(v => cronoNames.some(n => this.serviceEnabled(v, n))).length,
        sneCount: this.vehicles.filter(v => SNE_MODULES.some(n => this.serviceEnabled(v, n))).length
      }
    },
    summaries() {
      const scoped = this.scrapingServices.filter(s => APP_NAMES.includes(s.appName))

      return scoped.map(service => {
        const enabledCount = this.vehicles.filter(v => this.serviceEnabled(v, service.name)).length
        const state = enabledCount === 0
          ? 'none'
          : (enabledCount === this.vehicles.length ? 'all' : 'mixed')

        return {
          name: service.name,
          friendlyName: service.friendlyName || service.name,
          appName: service.appName,
          uf: service.uf,
          requiresCredential: Array.isArray(service.validateFields) &&
            service.validateFields.some(f => CREDENTIAL_FIELDS.includes(f)),
          enabledCount,
          state
        }
      })
    },
    filteredSummaries() {
      const generic = this.generic.trim().toLowerCase()
      const list = generic
        ? this.summaries.filter(s =>
          s.friendlyName.toLowerCase().includes(generic) || s.name.toLowerCase().includes(generic))
        : this.summaries

      return Sorter.sortBy([...list], 'friendlyName', { sensitivity: false })
    },
    toEnable() {
      return this.describe('enable')
    },
    toDisable() {
      return this.describe('disable')
    },
    productPhrases() {
      const frases = []
      if (this.cronoIntent === true) frases.push('Ativar o Cronotacógrafo')
      if (this.cronoIntent === false) frases.push('Remover o Cronotacógrafo')
      if (this.sneModule) frases.push(`Registrar o SNE — módulo ${this.sneModuleLabel}`)
      return frases
    },
    hasIntent() {
      return this.productPhrases.length > 0 || this.toEnable.length > 0 || this.toDisable.length > 0
    },
    failedItems() {
      return this.report.failed.map(f => `${f.plate} — ${f.reason}`)
    },
    pendingItems() {
      return this.report.pending.map(p => `${p.plate} — ${p.reason}`)
    }
  },
  data: () => ({
    aborted: null,
    cronoIntent: null,
    generic: '',
    intents: {},
    progress: null,
    report: null,
    sneModule: ''
  }),
  methods: {
    /**
     * Balão do ícone de informação. A classe é a do projeto: `.vue-tooltip` nasce
     * com z-index 100 e o `.modal` do bootstrap fica em 1050, então sem ela o
     * balão abre atrás do overlay e parece que não abriu.
     */
    tip(content) {
      return { content: content, class: 'tooltip-in-modal' }
    },
    serviceHint(summary) {
      const parts = [summary.appName, summary.name]
      if (summary.requiresCredential) parts.push('exige credencial')
      return parts.join(' · ')
    },
    serviceEnabled(vehicle, name) {
      const service = (vehicle.services || {})[name]
      return !!(service && service.enable)
    },
    mixed(count) {
      return count > 0 && count < this.vehicles.length
    },
    consolidated(count) {
      if (count === 0) return 'inativo em todos'
      if (count === this.vehicles.length) return `ativo nos ${this.vehicles.length}`
      return `${count} de ${this.vehicles.length}`
    },
    intentLabel(intent) {
      if (intent === 'enable' || intent === true) return 'Ligar'
      if (intent === 'disable' || intent === false) return 'Desligar'
      return 'Manter'
    },
    intentClass(intent) {
      if (intent === 'enable' || intent === true) return 'btn-primary'
      if (intent === 'disable' || intent === false) return 'btn-danger'
      return 'btn-outline'
    },
    /**
     * Três estados visíveis: manter, ligar, desligar.
     *
     * "Manter" tem duas formas internas — ausente (nunca tocado) e `keep`
     * (recusa explícita). A segunda existe para dizer ao backend "não reaplique
     * aqui a sugestão do SNE".
     */
    cycleIntent(name) {
      const atual = this.intents[name]
      const proximo = (!atual || atual === 'keep')
        ? 'enable'
        : (atual === 'enable' ? 'disable' : 'keep')

      this.$set(this.intents, name, proximo)
    },
    cycleCrono() {
      if (this.cronoIntent === null) this.cronoIntent = true
      else if (this.cronoIntent === true) this.cronoIntent = false
      else this.cronoIntent = null
    },
    /**
     * Prévia do que o backend vai desligar. Espelha BuildIntents#sne_intents na
     * API. Nenhum serviço da família SNE entra aqui: o módulo anterior é
     * substituído pelo contratado dentro de cada veículo, não desligado.
     */
    pickSneModule() {
      if (!this.sneModule) return

      const ufs = [...new Set(this.vehicles.map(v => (v.uf || '').toUpperCase()).filter(uf => uf))]

      this.scrapingServices
        .filter(s => s.appName === FROTAS_APP)
        .filter(s => !/sne/i.test(s.name))
        .filter(s => s.sphere === 'federal' || ufs.includes((s.uf || '').toUpperCase()))
        .forEach(s => this.$set(this.intents, s.name, 'disable'))
    },
    clearSne() {
      this.sneModule = ''
    },
    clearAll() {
      this.intents = {}
      this.cronoIntent = null
      this.sneModule = ''
    },
    describe(intent) {
      return Object.keys(this.intents)
        .filter(name => this.intents[name] === intent)
        .map(name => {
          const summary = this.summaries.find(s => s.name === name)
          return summary ? summary.friendlyName : name
        })
        .sort((a, b) => a.localeCompare(b, 'pt-BR'))
    },
    async apply() {
      const ids = this.vehicles.map(v => v.id)
      const report = { applied: [], failed: [], pending: [], unchanged: [] }
      const services = Object.keys(this.intents).length > 0 ? this.intents : undefined

      this.aborted = null
      this.progress = { done: 0, total: ids.length }

      for (let i = 0; i < ids.length; i += CHUNK) {
        const chunk = ids.slice(i, i + CHUNK)
        const payload = { account_id: this.accountId, vehicle_ids: chunk }

        if (services) payload.services = services
        if (this.cronoIntent !== null) payload.cm_crono = this.cronoIntent
        if (this.sneModule) payload.sne_module = this.sneModule

        try {
          // O interceptor de resposta devolve o corpo, então `data` já é o relatório.
          const response = await this.axios.put('admin/vehicles/services', payload)
          const data = response.data || {}

          report.applied.push(...(data.applied || []))
          report.failed.push(...(data.failed || []))
          report.pending.push(...(data.pending || []))
          report.unchanged.push(...(data.unchanged || []))
        } catch (e) {
          // Uma negativa de permissão vale para o lote inteiro: insistir só
          // multiplicaria o mesmo erro.
          const naoProcessados = ids.length - i
          const motivo = (e && e.errors) ? [].concat(e.errors).join(', ') : 'Ops.. Ocorreu um erro! Tente novamente.'
          this.aborted = `${motivo} — ${naoProcessados} veículos não foram processados.`
          this.$notify.addError(e)
          break
        } finally {
          this.progress.done = Math.min(this.progress.done + chunk.length, ids.length)
        }
      }

      this.progress = null
      this.report = report
      if (report.applied.length > 0) this.$emit('applied')
    },
    closeModal() {
      this.clearAll()
      this.report = null
      this.aborted = null
      this.progress = null
      this.generic = ''
      this.$emit('input', false)
    }
  },
  name: 'BulkServicesModal',
  props: {
    value: { default: false, type: Boolean },
    accountId: { required: true, type: Number },
    accountName: { default: '', type: String },
    scrapingServices: { required: true, type: Array },
    vehicles: { required: true, type: Array }
  }
}
</script>

<style scoped>
  .line {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 3px 0;
  }
  .service-row {
    border-bottom: 1px solid var(--border-color);
  }
  /* Larguras fixas mantêm badge, nome e contador alinhados em todas as linhas. */
  .state {
    flex: 0 0 86px;
    padding: 1px 6px;
    font-size: 0.78rem;
    line-height: 1.4;
  }
  .tag-slot {
    flex: 0 0 34px;
    text-align: center;
  }
  .label {
    flex: 0 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--primary-text-color);
  }
  .filler {
    flex: 1 1 auto;
  }
  .count {
    flex: 0 0 auto;
    white-space: nowrap;
    font-size: 0.75rem;
    color: var(--secondary-text-color);
  }
  .module {
    flex: 0 0 220px;
    height: auto;
    /* espaço à direita para a seta do custom-select do bootstrap */
    padding: 1px 24px 1px 6px;
    font-size: 0.78rem;
  }
  .hint {
    flex: 0 0 auto;
    color: var(--secondary-text-color);
    cursor: help;
    vertical-align: middle;
  }
  .block-title {
    margin-bottom: 2px;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--secondary-text-color);
  }
  .search .form-control {
    flex: 1 1 auto;
  }
  .services-list {
    max-height: 320px;
    overflow-y: auto;
    border-top: 1px solid var(--border-color);
  }
  .summary {
    margin-top: 10px;
    padding: 8px 10px;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    font-size: 0.8rem;
    color: var(--primary-text-color);
  }
  .summary p {
    margin-bottom: 2px;
  }
  .badge {
    display: inline-block;
    padding: 2px 5px;
    border-radius: 3px;
    font-size: 0.65rem;
    font-weight: 600;
    color: white;
  }
  .badge-secondary { background-color: #6c757d; }
</style>
